// The tagger roster — the tagging console's home.
//
// Everything rendered here is the backend registry's own declaration
// (`GET /api/bi/tagging/taggers`), so a tagger added to `TAGGERS` in
// `recruiter/tagging/registry.py` appears with no change to this file. That is
// the property the base class exists to give, and a per-tagger branch in this
// component would quietly take it away — so there are none.
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Badge } from "@bi/shared";

import { ErrorState, LoadingState } from "../components/states";
import { taggersQuery, taggingIdentityQuery, tagRunsQuery } from "../lib/queries";

/** ASYNC / BLOCKING is the tagger's own declaration of WHEN it runs, and it is
 *  the one thing on a card worth colour: a blocking tagger is awaited by an agent
 *  mid-turn, which is a different risk profile from one nothing waits on. */
function ModeBadge({ mode }: { mode: string }) {
  return (
    <Badge size="sm" tone={mode === "blocking" ? "warning" : "neutral"} variant="soft">
      {mode}
    </Badge>
  );
}

export function TaggersPage() {
  const taggers = useQuery(taggersQuery);
  const me = useQuery(taggingIdentityQuery);
  const runs = useQuery(tagRunsQuery());

  if (taggers.isPending) return <LoadingState label="Loading taggers…" />;
  if (taggers.isError) {
    return <ErrorState error={taggers.error} onRetry={() => void taggers.refetch()} />;
  }

  const rows = taggers.data;
  const live = (runs.data ?? []).filter(
    (r) => r.status === "queued" || r.status === "running",
  ).length;
  const activeTags = rows.reduce((n, t) => n + t.nActiveTags, 0);

  return (
    <div className="bi-page">
      <div className="bi-page__head">
        <h1 className="bi-page__title">Taggers</h1>
        <p className="bi-page__sub">
          A tagger reads one surface and answers a set of typed questions about it. Adding a
          question is a row, not a deploy.
          {me.data && !me.data.canWrite
            ? " You hold read access, so the controls that spend are hidden."
            : ""}
        </p>
      </div>

      {/* The counts a reader opens on. A rate with no denominator is the recurring
          defect in every report this project has produced, so the denominators are
          on the page before any rate is. */}
      <div className="bi-panel">
        <div className="bi-stats">
          <div>
            <div className="bi-stat__value">{rows.length}</div>
            <div className="bi-stat__label">taggers</div>
          </div>
          <div>
            <div className="bi-stat__value">{activeTags}</div>
            <div className="bi-stat__label">active tags</div>
          </div>
          <div>
            <div className="bi-stat__value">{runs.data?.length ?? "-"}</div>
            <div className="bi-stat__label">runs</div>
          </div>
          <div>
            <div className="bi-stat__value">{live}</div>
            <div className="bi-stat__label">running now</div>
          </div>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="bi-panel" style={{ marginTop: "var(--rc-space-4)" }}>
          <div className="bi-empty">
            <p style={{ margin: 0, fontWeight: 500 }}>No taggers registered yet.</p>
            <p style={{ maxWidth: "60ch", margin: "0 auto" }}>
              The framework is built and empty on purpose. The taggers that go here are the ones
              already running in the product — the per-turn triage, the offer verdict, the intent
              read, the package grade — each arriving by reporting from the call it already makes,
              without a prompt being touched.
            </p>
          </div>
        </div>
      ) : null}

      <div className="bi-tagger-grid" style={{ marginTop: "var(--rc-space-4)" }}>
        {rows.map((t) => (
          <Link
            key={t.name}
            to="/taggers/$name"
            params={{ name: t.name }}
            className="bi-tagger-card"
          >
            <div className="bi-tagger-card__head">
              <span className="bi-tagger-card__title">{t.label}</span>
              <ModeBadge mode={t.mode} />
            </div>
            <span className="bi-tagger-card__desc">{t.description}</span>
            <div className="bi-tagger-card__meta">
              <span>
                {t.nActiveTags} of {t.nTags} tag{t.nTags === 1 ? "" : "s"} active
              </span>
              <span>·</span>
              <span>{t.subjectKind}</span>
              <span>·</span>
              {/* The difference a reader needs before clicking in: one of these
                  you can run and edit, the other you can only watch. */}
              <span>{t.ownsCall ? "live + runnable" : "live only"}</span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
